/**
 * Mapa do grupo — onde está cada pessoa, e os lugares do dia.
 *
 * Três estados, como no design:
 *   1. pedir — a primeira vez: explica o que é, o que a pessoa ganha e o que
 *      NÃO acontece (sem histórico, só com o app aberto). "Agora não" leva
 *      ao estado 3, não a um limbo.
 *   2. ativo — mapa com pins: gente (com "há X min"), atividades do dia com
 *      lugar, hospedagem. Filtros: Todos · Minha família · Lugares.
 *   3. desligado — explica a reciprocidade e oferece ligar. Os lugares
 *      continuam disponíveis: são da viagem, não da pessoa.
 *
 * Como PWA, a posição só é enviada com o app aberto. Por isso a etiqueta é
 * sempre "há quanto tempo", e uma posição velha fica cinza — nunca some, mas
 * nunca finge ser atual.
 */
import { featureEnabled, hasCoordinates, resolveToday } from '@nv/core';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { useAgora } from '../../demo/DemoContext';
import { useTheme } from '../../theme/ThemeProvider';
import { useDiasDaViagem } from '../../trip/dias';
import { useTrip } from '../../trip/TripContext';
import { tripInputDaViagem } from '../../trip/tripInput';
import { Icon } from '../../ui/icons';
import { FotoAvatar } from '../perfil/FotoAvatar';
import { Card, EmptyState, SectionLabel } from '../../ui/primitives';
import { ComoChegar } from './ComoChegar';
import { useEncontro, useInvalidarEncontro } from '../encontro/api';
import { EncontroCard } from '../encontro/EncontroCard';
import { useCarro, useInvalidarCarro } from '../carro/api';
import { CarroNaHome } from '../carro/CarroNaHome';
import { MarcarEncontro } from '../encontro/MarcarEncontro';
import { useCompartilharPosicao } from './compartilhar';
import { type LugarNoMapa, MapaGoogle } from './MapaGoogle';
import { type PessoaNoMapa, usePosicoes } from './posicoes';
import { diaDoMapa, frescor, haQuanto } from './regras';

type Filtro = 'todos' | 'familia' | 'lugares';

const chaveDecisao = (tripId: string) => `nv.mapa.decidiu:${tripId}`;
function jaDecidiu(tripId: string): boolean {
  try {
    return localStorage.getItem(chaveDecisao(tripId)) === '1';
  } catch {
    return false;
  }
}
function marcarDecisao(tripId: string) {
  try {
    localStorage.setItem(chaveDecisao(tripId), '1');
  } catch {
    // sem storage, pergunta de novo da próxima vez — aceitável
  }
}

interface AtividadeDoDia {
  id: string;
  title: string;
  startTime: string;
  locationName: string | null;
  placeId: string | null;
  latitude: number | null;
  longitude: number | null;
  deletedAt: string | null;
}

async function lerDia(dayId: string): Promise<{ activities: AtividadeDoDia[] }> {
  const res = await fetch(`/api/v1/days/${dayId}`, { credentials: 'same-origin' });
  if (!res.ok) throw new Error(`Falha ao carregar o dia (${res.status})`);
  return (await res.json()) as { activities: AtividadeDoDia[] };
}

async function lerConfig(): Promise<{ maps: { key: string; mapId: string | null } | null }> {
  const res = await fetch('/api/v1/config', { credentials: 'same-origin' });
  if (!res.ok) return { maps: null };
  return (await res.json()) as { maps: { key: string; mapId: string | null } | null };
}

export function MapaScreen() {
  const { trip, tripId, members, meuMemberId } = useTrip();
  const { name: tema } = useTheme();
  const viagem = tripInputDaViagem(trip, members.length);
  const now = useAgora(viagem);
  const agora = now.getTime();
  const dias = useDiasDaViagem(tripId);

  const compartilhar = useCompartilharPosicao();
  const ativo = compartilhar.ativo && compartilhar.tripId === tripId;
  const [decidiu, setDecidiu] = useState(() => jaDecidiu(tripId));
  const [filtro, setFiltro] = useState<Filtro>('todos');
  const [enquadrar, setEnquadrar] = useState(0);
  const [centralizar, setCentralizar] = useState<{ latitude: number; longitude: number; pedido: number } | null>(null);

  useEffect(() => {
    compartilhar.retomar(tripId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId]);

  const config = useQuery({ queryKey: ['config'], queryFn: lerConfig, staleTime: Infinity });
  const posicoes = usePosicoes(tripId, ativo);

  // O dia do mapa: hoje, dentro da viagem; senão a borda mais próxima.
  const hoje = useMemo(() => {
    if (!viagem) return null;
    try {
      return resolveToday(viagem.timeZone, now, viagem.utcOffsetSchedule);
    } catch {
      return null;
    }
  }, [viagem, now]);
  const dataDoMapa = diaDoMapa(dias.days ?? [], hoje);
  const diaId = dias.days?.find((d) => d.date === dataDoMapa)?.id ?? null;
  const dia = useQuery({ queryKey: ['mapa', 'dia', diaId], queryFn: () => lerDia(diaId ?? ''), enabled: !!diaId, staleTime: 60_000 });

  const encontroLigado = featureEnabled(trip.features, 'ponto_de_encontro');
  const encontro = useEncontro(tripId, encontroLigado);
  useInvalidarEncontro(tripId);
  // `/mapa?encontro=1` chega do "+" da barra (lote de aparência 1).
  const [busca] = useSearchParams();
  const [marcando, setMarcando] = useState(busca.get('encontro') === '1');
  const carroLigado = featureEnabled(trip.features, 'carro');
  const carro = useCarro(tripId, carroLigado);
  useInvalidarCarro(tripId);

  const lugares: LugarNoMapa[] = useMemo(() => {
    const lista: LugarNoMapa[] = [];
    const car = carro.data?.car;
    if (car) {
      const d = new Date(car.parkedAt);
      lista.push({ id: `carro-${car.id}`, titulo: car.note ?? 'Carro', latitude: car.latitude, longitude: car.longitude, tipo: 'carro', hora: `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}` });
    }
    const enc = encontro.data?.meeting;
    if (enc) {
      const d = new Date(enc.at);
      lista.push({ id: `encontro-${enc.id}`, titulo: enc.title, latitude: enc.latitude, longitude: enc.longitude, tipo: 'encontro', hora: `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}` });
    }
    if (
      trip.accommodationLatitude !== null &&
      trip.accommodationLongitude !== null &&
      hasCoordinates({ latitude: trip.accommodationLatitude, longitude: trip.accommodationLongitude })
    ) {
      lista.push({
        id: 'hospedagem',
        titulo: trip.accommodationName ?? 'Hospedagem',
        latitude: trip.accommodationLatitude,
        longitude: trip.accommodationLongitude,
        tipo: 'hospedagem',
      });
    }
    for (const a of dia.data?.activities ?? []) {
      if (a.deletedAt || !hasCoordinates(a)) continue;
      lista.push({ id: a.id, titulo: a.locationName ?? a.title, latitude: a.latitude, longitude: a.longitude, tipo: 'atividade', hora: a.startTime });
    }
    return lista;
  }, [trip, dia.data, encontro.data, carro.data]);

  const cores = useMemo(() => Object.fromEntries(members.map((m) => [m.memberId, m.familyColor])), [members]);
  const fotos = useMemo(() => Object.fromEntries(members.map((m) => [m.memberId, m.avatarUrl ?? null])), [members]);
  const minhaFamilia = members.find((m) => m.memberId === meuMemberId)?.familyId;
  const familiaDe = useMemo(() => new Map(members.map((m) => [m.memberId, m])), [members]);

  const pessoas: PessoaNoMapa[] = useMemo(() => {
    const todas = posicoes.data?.people ?? [];
    if (filtro === 'lugares') return [];
    if (filtro === 'familia') return todas.filter((p) => p.me || (minhaFamilia && familiaDe.get(p.memberId)?.familyId === minhaFamilia));
    return todas;
  }, [posicoes.data, filtro, minhaFamilia, familiaDe]);

  const lugaresVisiveis = filtro === 'familia' ? [] : lugares;
  const mapId = config.data?.maps?.mapId ?? null;
  const temMaps = config.isSuccess ? config.data.maps !== null : true;

  const outrasNoMapa = (posicoes.data?.people ?? []).filter((p) => !p.me).length;

  // ── Estado 1: pedir ──
  if (!ativo && !decidiu && compartilhar.permissao !== 'negada') {
    return (
      <section className="screen mapa">
        <h1 className="mapa__titulo">Mapa do grupo</h1>
        <div className="mapa__pedido">
          <span className="mapa__pulso" aria-hidden="true" />
          <h2 className="mapa__pedido-titulo">Onde está cada pessoa</h2>
          <p className="mapa__pedido-texto">
            Compartilhe a sua posição para ver a das outras pessoas do grupo. Vale
            enquanto o app está aberto — o celular não é rastreado em segundo plano.
          </p>
          <ul className="mapa__garantias">
            <li>Só quem está nesta viagem vê.</li>
            <li>Sem histórico: fica só a última posição, com "há quanto tempo".</li>
            <li>Desligar apaga a posição na hora.</li>
          </ul>
          <button
            type="button"
            className="nv-btn nv-btn--primary nv-btn--block"
            onClick={() => {
              marcarDecisao(tripId);
              setDecidiu(true);
              void compartilhar.ligar(tripId);
            }}
          >
            Continuar
          </button>
          <button
            type="button"
            className="nv-btn nv-btn--ghost nv-btn--block"
            onClick={() => {
              marcarDecisao(tripId);
              setDecidiu(true);
            }}
          >
            Agora não
          </button>
        </div>
        <LugaresDoDia lugares={lugares} data={dataDoMapa} />
      </section>
    );
  }

  // ── Estado 3: desligado ──
  if (!ativo) {
    return (
      <section className="screen mapa">
        <header className="mapa__cabecalho">
          <h1 className="mapa__titulo">Mapa do grupo</h1>
          <span className="mapa__chip mapa__chip--off">● Localização desligada</span>
        </header>
        <Card>
          <p className="mapa__desligado-titulo">Você não está compartilhando sua localização</p>
          <p className="mapa__desligado-texto">
            {compartilhar.permissao === 'negada'
              ? 'O navegador negou o acesso à localização. Libere nas configurações do site e tente de novo.'
              : compartilhar.permissao === 'indisponivel'
                ? 'Este aparelho não oferece localização ao app.'
                : 'Sem compartilhar, você também não vê onde estão as outras pessoas.'}
          </p>
          {compartilhar.permissao !== 'indisponivel' && (
            <button type="button" className="nv-btn nv-btn--primary nv-btn--block" onClick={() => void compartilhar.ligar(tripId)}>
              Começar a compartilhar
            </button>
          )}
        </Card>
        <div className="mapa__quadro mapa__quadro--vazio" role="img" aria-label="Mapa indisponível">
          <p>Mapa indisponível enquanto a localização estiver desligada.</p>
        </div>
        {encontroLigado && encontro.data?.meeting && <EncontroCard encontro={encontro.data.meeting} />}
        {encontroLigado && (
          <button type="button" className="nv-btn nv-btn--secondary nv-btn--block" onClick={() => setMarcando(true)} data-testid="marcar-encontro">
            <Icon name="ponto" size={18} />
            Marcar ponto de encontro
          </button>
        )}
        {marcando && <MarcarEncontro onFechar={() => setMarcando(false)} />}
        <CarroNaHome />
        <LugaresDoDia lugares={lugares} data={dataDoMapa} />
      </section>
    );
  }

  // ── Estado 2: ativo ──
  return (
    <section className="screen mapa">
      <header className="mapa__cabecalho">
        <div>
          <h1 className="mapa__titulo">Mapa do grupo</h1>
          <span className="mapa__chip mapa__chip--on">
            ● Compartilhando{compartilhar.permissao === 'pedindo' ? '…' : ''}
          </span>
        </div>
        <button
          type="button"
          className="mapa__toggle mapa__toggle--on"
          role="switch"
          aria-checked="true"
          aria-label="Compartilhar minha localização"
          onClick={() => void compartilhar.desligar()}
        >
          <span className="mapa__toggle-bola" />
        </button>
      </header>

      {compartilhar.erro && <p className="mapa__erro">{compartilhar.erro}</p>}

      <div className={`mapa__area${temMaps ? '' : ' mapa__area--sem'}`}>
        {config.isSuccess && (
          <MapaGoogle
            key={`${tema}:${mapId ?? ''}`}
            mapId={mapId}
            tema={tema}
            pessoas={pessoas}
            lugares={lugaresVisiveis}
            cores={cores}
            fotos={fotos}
            agora={agora}
            enquadrar={enquadrar}
            centralizarEm={centralizar}
          />
        )}
        <div className="mapa__botoes">
          <button type="button" className="mapa__botao" aria-label="Enquadrar tudo" onClick={() => setEnquadrar((n) => n + 1)}>
            ⤢
          </button>
          <button
            type="button"
            className="mapa__botao"
            aria-label="Minha posição"
            disabled={!compartilhar.ultima}
            onClick={() =>
              compartilhar.ultima &&
              setCentralizar({ latitude: compartilhar.ultima.latitude, longitude: compartilhar.ultima.longitude, pedido: Date.now() })
            }
          >
            ◎
          </button>
        </div>
        <span className="mapa__nota">só com o app aberto · atualiza a cada 20 s</span>
      </div>

      {encontroLigado && encontro.data?.meeting && <EncontroCard encontro={encontro.data.meeting} />}
      {encontroLigado && (
        <button type="button" className="nv-btn nv-btn--secondary nv-btn--block" onClick={() => setMarcando(true)} data-testid="marcar-encontro">
          <Icon name="ponto" size={18} />
          {encontro.data?.meeting ? 'Marcar outro ponto de encontro' : 'Marcar ponto de encontro'}
        </button>
      )}
      {marcando && <MarcarEncontro onFechar={() => setMarcando(false)} />}
      <CarroNaHome />

      <div className="chip-row mapa__filtros" role="tablist" aria-label="Filtro">
        {(
          [
            ['todos', `Todos · ${outrasNoMapa + 1}`],
            ['familia', 'Minha família'],
            ['lugares', 'Lugares'],
          ] as [Filtro, string][]
        ).map(([valor, rotulo]) => (
          <button
            type="button"
            key={valor}
            role="tab"
            aria-selected={filtro === valor}
            className={filtro === valor ? 'pick pick--on' : 'pick'}
            onClick={() => setFiltro(valor)}
          >
            {rotulo}
          </button>
        ))}
      </div>

      {filtro !== 'lugares' && (
        <Card>
          {pessoas.length === 0 && posicoes.isSuccess && (
            <p className="mapa__vazio">
              {filtro === 'familia' ? 'Ninguém da sua família está compartilhando agora.' : 'Só você por enquanto. Quem ligar o mapa aparece aqui.'}
            </p>
          )}
          {[...pessoas]
            .sort((a, b) => Number(b.me) - Number(a.me) || a.name.localeCompare(b.name))
            .map((p) => {
              const estado = frescor(p.recordedAt, agora);
              const membro = familiaDe.get(p.memberId);
              return (
                <div key={p.memberId} className={`mapa__pessoa mapa__pessoa--${estado}`}>
                  <FotoAvatar nome={p.name} url={membro?.avatarUrl} cor={membro?.familyColor} tamanho={34} />
                  <span className="mapa__pessoa-texto">
                    <span className="mapa__pessoa-nome">{p.me ? 'Você' : p.name}</span>
                    {membro?.familyName && <span className="mapa__pessoa-familia">{membro.familyName}</span>}
                  </span>
                  <span className="mapa__quando">{haQuanto(p.recordedAt, agora)}</span>
                </div>
              );
            })}
        </Card>
      )}

      {filtro !== 'familia' && <LugaresDoDia lugares={lugares} data={dataDoMapa} />}
    </section>
  );
}

function LugaresDoDia({ lugares, data }: { lugares: LugarNoMapa[]; data: string | null }) {
  return (
    <>
      <SectionLabel>{data ? `LUGARES · ${data.slice(8, 10)}/${data.slice(5, 7)}` : 'LUGARES'}</SectionLabel>
      {lugares.length === 0 ? (
        <EmptyState
          ilustracao="mapa"
          title="Nenhum lugar no mapa hoje"
          description="Atividades com local escolhido pela busca e a hospedagem com endereço aparecem aqui."
        />
      ) : (
        <Card>
          {lugares.map((l) => {
            return (
              <div key={l.id} className="mapa__lugar">
                <span className="mapa__lugar-icone" aria-hidden="true">
                  <Icon name={l.tipo === 'hospedagem' ? 'home' : 'local'} size={18} />
                </span>
                <span className="mapa__pessoa-texto">
                  <span className="mapa__pessoa-nome">{l.titulo}</span>
                  <span className="mapa__pessoa-familia">{l.tipo === 'hospedagem' ? 'Hospedagem' : `Atividade · ${l.hora}`}</span>
                </span>
                {l.tipo === 'atividade' && data ? (
                  <Link className="nv-btn nv-btn--chip" to={`/roteiro/${data}/${l.id}`}>
                    Abrir
                  </Link>
                ) : (
                  <ComoChegar destino={{ locationName: l.titulo, latitude: l.latitude, longitude: l.longitude }} className="nv-btn nv-btn--chip" />
                )}
              </div>
            );
          })}
        </Card>
      )}
    </>
  );
}
